import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from './shared/LoadingSpinner';
import { EmptyState } from './shared/EmptyState';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAdmin?: boolean;
    /** Where unauthenticated visitors are sent. Defaults to /login. */
    unauthenticatedRedirect?: string | (() => string);
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
    children, 
    requireAdmin = false,
    unauthenticatedRedirect = '/login',
}) => {
    const { isAuthenticated, isAdmin, loading } = useAuth();

    if (loading) {
        return <LoadingSpinner fullScreen text="Loading..." />;
    }

    if (!isAuthenticated) {
        const target = typeof unauthenticatedRedirect === 'function'
            ? unauthenticatedRedirect()
            : unauthenticatedRedirect;
        return <Navigate to={target} replace />;
    }

    if (requireAdmin && !isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
                <div className="max-w-md w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-gray-200">
                    <EmptyState
                        icon={
                            <svg 
                                className="mx-auto h-16 w-16 text-red-500" 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                                />
                            </svg>
                        }
                        title="Access Denied"
                        message="You don't have permission to access this page. Admin access is required."
                        action={{
                            label: 'Go Back',
                            onClick: () => window.history.back()
                        }}
                    />
                </div>
            </div>
        );
    }

    return <>{children}</>;
};

export default ProtectedRoute;
